import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, FileText } from 'lucide-react'
import { 
  ChevronDown, 
  Loader2, 
  AlertCircle,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Star,
  Globe,
  MapPin,
  Phone,
  Mail,
  ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { OPENAI_SUPPORTED_LANGUAGES, getLanguageByCode, getDefaultLanguage } from '../../utils/languages';
import { resolveStyleConfig, parseRawConfig, ResolvedStyleConfig, overlayColorToRgba } from '../../utils/publicMenuStyleUtils';
import { getAllergenDisplayNameItalian, getAllergenDescriptionItalian } from '../../utils/allergenDisplayNames';
import { getPublicAllergenIconPath } from '../../utils/publicAllergens';
import AllergenIcon from '../../components/menu/AllergenIcon';
import { sortMenuByPosition } from '../../utils/menuSortUtils';
import { usePublicMenuMeta } from '../../hooks/usePublicMenuMeta';

// Define types (keeping your existing types)
interface Allergen {
  id: number;
  name: string;
  code?: string;
  icon?: string;
  description?: string;
}

interface Restaurant {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  city?: string;
  country?: string;
  logoUrl?: string;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    tripadvisor?: string;
    google?: string;
  };
}

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price?: string | number;
  imageUrl?: string;
  isVegetarian?: boolean;
  isVegan?: boolean;
  isSpicy?: boolean;
  allergens?: Allergen[];
  Allergens?: Allergen[];
}

interface Category {
  id: number;
  name: string;
  description?: string;
  position?: number;
  items?: MenuItem[];
  MenuItems?: MenuItem[];
}

interface MenuSection {
  id: number;
  name: string;
  description?: string;
  position?: number;
  Categories?: Category[];
}

interface MenuData {
  id: number;
  title: string;
  slug: string;
  description?: string;
  language?: string;
  restaurant?: Restaurant;
  MenuSections?: MenuSection[];
  categories?: Category[];
  styleConfig?: any;
  latestPdfUrl?: string;
  latestPdfLanguage?: string;
  latestPdfTemplate?: string;
}

// Helper function to get full image URL
const getFullImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath) return '';
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) {
    return imagePath;
  }
  
  if (imagePath.startsWith('/uploads/')) {
    const baseUrl = window.location.origin;
    return `${baseUrl}${imagePath}`;
  }
  
  const baseUrl = window.location.origin;
  return `${baseUrl}/${imagePath}`;
};

function determineBackgroundImage(cfg: any): string | null {
  if (cfg?.backgroundImage) return cfg.backgroundImage;
  return '/dark2.jpeg'; // Default for Dark theme
}

// Get all categories from menu data
const getAllCategories = (menuData: MenuData): Category[] => {
  const categories: Category[] = [];
  
  if (menuData.MenuSections) {
    menuData.MenuSections.forEach(section => {
      if (section.Categories) {
        section.Categories.forEach(category => {
          categories.push({
            ...category,
            items: category.items || category.MenuItems || []
          });
        });
      }
    });
  }
  
  if (menuData.categories) {
    menuData.categories.forEach(category => {
      categories.push({
        ...category,
        items: category.items || category.MenuItems || []
      });
    });
  }
  
  return categories;
};

// Allergen Modal Component - MODIFIED to use menuId
const AllergenModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  styleConfig: ResolvedStyleConfig;
  menuId: number; // Added menuId prop
}> = ({ isOpen, onClose, styleConfig, menuId }) => {
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && menuId) {
      fetchAllergens();
    }
  }, [isOpen, menuId]);

  const fetchAllergens = async () => {
    try {
      setLoading(true);
      // MODIFIED: Now using menuId in the API endpoint
      const response = await axios.get(`allergens/menu/${menuId}`);
      if (response.data.success) {
        setAllergens(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching allergens:', error);
      toast.error('Impossibile caricare le informazioni sugli allergeni');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-2xl flex flex-col"
        style={{ 
          backgroundColor: '#000000',
          color: '#ffffff'
        }}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#aaaaaa]">
          <h2 
            className="text-2xl font-bold"
            style={{ 
              fontFamily: styleConfig.fontFamily,
              color: '#ffffff'
            }}
          >
            Lista allergeni
          </h2>
          <button
            onClick={onClose}
            className="text-2xl hover:opacity-70 transition-opacity"
            style={{ color: '#ffffff' }}
            aria-label="Chiudi"
          >
            &times;
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#ffffff' }} />
              <span className="ml-3" style={{ color: '#ffffff' }}>Caricamento allergeni...</span>
            </div>
          ) : allergens.length === 0 ? (
            <div className="text-center py-12">
              <p style={{ color: '#ffffff' }}>Nessuna informazione sugli allergeni disponibile.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {allergens.map((allergen) => (
                <div 
                  key={allergen.id}
                  className="p-4 rounded-lg border border-[#aaaaaa] bg-[#aaaaaa]"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <AllergenIcon allergen={allergen} alt={getAllergenDisplayNameItalian(allergen)} className="w-6 h-6" />
                    <h3 
                      className="text-lg font-semibold"
                      style={{ 
                        fontFamily: styleConfig.fontFamily,
                        color: '#ffffff'
                      }}
                    >
                      {getAllergenDisplayNameItalian(allergen)}
                    </h3>
                    {allergen.code && (
                      <span 
                        className="text-sm px-2 py-1 rounded bg-[#aaaaaa] text-[#aaaaaa]"
                      >
                        {allergen.code}
                      </span>
                    )}
                  </div>
                  {getAllergenDescriptionItalian(allergen) && (
                    <p className="mt-2 text-[#aaaaaa]">{getAllergenDescriptionItalian(allergen)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-[#aaaaaa] text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-md font-medium hover:opacity-90 transition-opacity text-white"
            style={{ backgroundColor: styleConfig.secondaryColor }}
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

const PublicMenuPageDark: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>(searchParams.get('lang') || 'it');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [styleConfig, setStyleConfig] = useState<ResolvedStyleConfig>(() => resolveStyleConfig(undefined));
  const [showAllergenModal, setShowAllergenModal] = useState(false);
  const [visibleAllergenKey, setVisibleAllergenKey] = useState<string | null>(null);
  const [pageKey, setPageKey] = useState(0);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = getLanguageByCode(selectedLanguage) || getDefaultLanguage();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize default selections when menu data loads
  useEffect(() => {
    if (menuData) {
      const sections = menuData.MenuSections || [];
      const allCategories = getAllCategories(menuData);
      
      if (sections.length > 0) {
        const firstSection = sections[0];
        setSelectedSection(firstSection.name);
        
        if (firstSection.Categories && firstSection.Categories.length > 0) {
          const firstCategory = firstSection.Categories[0];
          setSelectedCategory(firstCategory.name);
        }
      } else if (allCategories.length > 0) {
        const firstCategory = allCategories[0];
        setSelectedCategory(firstCategory.name);
      }
    }
  }, [menuData]);

  useEffect(() => {
    if (slug) {
      const langFromUrl = searchParams.get('lang');
      if (langFromUrl) {
        setSelectedLanguage(langFromUrl);
        fetchMenuData(langFromUrl);
      } else {
        fetchMenuData(selectedLanguage);
      }
    }
  }, [slug]);

  usePublicMenuMeta(menuData?.restaurant?.name);

  const fetchMenuData = async (lang: string, retryCount = 0) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`/menu/public/${slug}`, {
        params: { lang }
      });
      
      if (response.data.success) {
        const data: MenuData = sortMenuByPosition(response.data.data) as MenuData;
        setMenuData(data);
        
        if (data.language && !selectedLanguage) {
          setSelectedLanguage(data.language);
        }
        
        const resolved = resolveStyleConfig(data.styleConfig);
        const rawCfg = parseRawConfig(data.styleConfig);
        const bgImage = resolved.backgroundImage || determineBackgroundImage(rawCfg);
        setStyleConfig({ ...resolved, backgroundImage: bgImage });

        const pendingLang = response.headers?.['x-translation-pending'];
        if (pendingLang && retryCount < 2) {
          setTimeout(() => fetchMenuData(lang, retryCount + 1), 3500);
        }
      } else {
        setError('Menu not found');
      }
    } catch (error: any) {
      console.error('Error fetching menu:', error);
      setError(error.response?.data?.message || 'Failed to load menu');
    } finally {
      setLoading(false);
      setTranslating(false);
    }
  };

  const handleLanguageChange = async (langCode: string) => {
    if (langCode === selectedLanguage) {
      setIsLanguageDropdownOpen(false);
      return;
    }

    setTranslating(true);
    setSelectedLanguage(langCode);
    setIsLanguageDropdownOpen(false);
    
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('lang', langCode);
    window.history.pushState({}, '', newUrl.toString());
    
    await fetchMenuData(langCode);
  };

  // Get menu sections for tabs
  const getMenuSections = () => {
    if (!menuData) return [];
    
    const sections = menuData.MenuSections || [];
    
    if (sections.length > 0) {
      return sections;
    }
    
    // If no sections, create one from categories
    const allCategories = getAllCategories(menuData);
    if (allCategories.length > 0) {
      return [{
        id: 1,
        name: 'Menu',
        Categories: allCategories.map((cat, index) => ({
          ...cat,
          id: cat.id || index + 1
        }))
      }];
    }
    
    return [];
  };

  // Get categories for selected section
  const getCategoriesForSection = (sectionName: string) => {
    const sections = getMenuSections();
    const section = sections.find(s => s.name === sectionName);
    
    if (!section || !section.Categories) return [];
    
    return section.Categories.filter(cat => 
      !cat.name.toLowerCase().includes('all categories') && 
      !cat.name.toLowerCase().includes('tutte le categorie') &&
      !cat.name.toLowerCase().includes('tutti')
    );
  };

  // Get items for selected category
  const getItemsForCategory = () => {
    if (!selectedCategory || !menuData) return [];
    
    const sections = getMenuSections();
    
    for (const section of sections) {
      if (section.Categories) {
        for (const category of section.Categories) {
          if (category.name === selectedCategory) {
            return category.items || category.MenuItems || [];
          }
        }
      }
    }
    
    return [];
  };

  if (loading && !menuData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-white text-lg">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error || !menuData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Menu Not Available</h1>
          <p className="text-[#aaaaaa] mb-6">
            {error || 'This menu is currently unavailable.'}
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-white hover:bg-[#aaaaaa] transition"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  const sections = getMenuSections();
  const categories = selectedSection ? getCategoriesForSection(selectedSection) : [];
  const items = getItemsForCategory();
  const restaurant = menuData.restaurant;
  const rawLogoUrl = restaurant?.logoUrl || styleConfig.logoUrl;
  const restaurantLogoUrl = rawLogoUrl ? getFullImageUrl(rawLogoUrl) : '';
  const logoSize = styleConfig.logoSize || 'medium';
  const logoHeightHeader = { small: 32, medium: 40, large: 56 }[logoSize] ?? 40;
  const logoHeightFooter = { small: 40, medium: 52, large: 72 }[logoSize] ?? 52;
  const socialLinks = restaurant?.socialLinks || {};
  const menuUrl = window.location.href;
  const overlayOpacity = styleConfig.overlayOpacity ?? 0;
  const showOverlay = overlayOpacity > 0;

  return (
    <div
      className="min-h-screen relative"
      style={{
        fontFamily: styleConfig.fontFamily,
        color: styleConfig.textColor,
        fontSize: styleConfig.fontSizeBase,
        lineHeight: styleConfig.lineHeight,
        backgroundColor: styleConfig.backgroundColor,
        ...(styleConfig.backgroundImage ? {
          backgroundImage: "url('/dark2.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        } : {}),
      }}
    >
      {/* Configurable overlay layer - above background, below content */}
      {showOverlay && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            zIndex: 1,
            background: overlayColorToRgba(styleConfig.overlayColor ?? '#000000', overlayOpacity)
          }}
          aria-hidden
        />
      )}
      {/* Content layer - above overlay */}
      <div className="relative z-10">
      {/* Header */}
      <header className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Restaurant Logo and Name - Left side */}
            <div className="flex items-center gap-3 flex-shrink-0 max-w-[70%]">
              {restaurantLogoUrl && (
                <img
                  src={restaurantLogoUrl}
                  alt={restaurant?.name || 'Restaurant'}
                  className="w-auto object-contain"
                  style={{ height: logoHeightHeader }}
                />
              )}
            </div>

            {/* Language Selector - Right side */}
            <div className="relative flex-shrink-0" ref={languageDropdownRef}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 
                         border border-[#aaaaaa] hover:border-[#aaaaaa] 
                         transition-colors rounded-lg bg-white text-black"
                disabled={translating}
              >
                {translating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Load...</span>
                  </>
                ) : (
                  <>
                    <span className="text-xl leading-none">
                      {currentLanguage.flag}
                    </span>
                    <span className="text-sm font-medium hidden md:inline">
                      {currentLanguage.nativeName}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        isLanguageDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </>
                )}
              </button>

              {isLanguageDropdownOpen && !translating && (
                <div
                  className="
                    absolute right-0 mt-1 w-52 md:w-64
                    bg-white text-black border border-[#aaaaaa] shadow-lg rounded-lg z-50
                  "
                >
                  <div className="max-h-64 overflow-y-auto py-2">
                    {OPENAI_SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2
                          hover:bg-[#aaaaaa] transition-colors
                          ${selectedLanguage === lang.code ? 'bg-[#aaaaaa]' : ''}
                        `}
                      >
                        <span className="text-2xl leading-none">
                          {lang.flag}
                        </span>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-sm md:text-base">
                            {lang.nativeName}
                          </div>
                          <div className="text-xs text-[#aaaaaa]0 hidden md:block">
                            {lang.name}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="flex overflow-x-auto py-2"
            style={{
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <style>
              {`
                .scroll-hide::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            <div className="scroll-hide flex justify-center md:justify-start">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    setPageKey(prev => prev + 1);
                    setSelectedSection(section.name);
                    const sectionCategories = getCategoriesForSection(section.name);
                    if (sectionCategories.length > 0) {
                      setSelectedCategory(sectionCategories[0].name);
                    }
                  }}
                  className={`flex-shrink-0 px-4 py-2 md:px-6 md:py-3 text-[24px] font-medium transition-all duration-200 whitespace-nowrap ${
                    selectedSection === section.name
                      ? 'text-white border-b-4 font-bold'
                      : 'text-[#aaaaaa] hover:text-white'
                  }`}
                  style={{
                    color: selectedSection === section.name ? styleConfig.headerColor : styleConfig.descriptionColor,
                    fontSize: styleConfig.fontSizeMacroSection ?? styleConfig.fontSizeXLarge,
                    fontFamily: styleConfig.fontFamilyMacroSection ?? styleConfig.fontFamily,
                    borderBottomColor: selectedSection === section.name ? styleConfig.secondaryColor : 'transparent'
                  }}
                >
                  {section.name}
                </button>
              ))}
            </div>
          </div>
          
          {/* Category Sub-navigation */}
          {selectedSection && categories.length > 0 && (
            <div className="px-4 md:px-6 py-3">
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {categories.map((category) => {
                  const isActive = selectedCategory === category.name;
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        if (selectedCategory !== category.name) {
                          setPageKey(prev => prev + 1);
                          setSelectedCategory(category.name);
                        }
                      }}
                      className={`
                        px-4 py-2 text-[15px] font-medium rounded-full border transition-all
                        ${
                          isActive
                            ? 'text-black border-white shadow-sm'
                            : 'bg-white text-black border-white hover:bg-[#aaaaaa]'
                        }
                      `}
                      style={{
                        backgroundColor: isActive ? styleConfig.secondaryColor : styleConfig.cardBackground,
                        color: isActive ? '#ffffff' : styleConfig.categoryColor,
                        borderColor: isActive ? styleConfig.secondaryColor : styleConfig.cardBorderColor,
                        fontSize: styleConfig.fontSizeCategory ?? styleConfig.fontSizeSmall,
                        fontFamily: styleConfig.fontFamilyCategory ?? styleConfig.fontFamily
                      }}
                    >
                      {category.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 overflow-x-hidden">
        <div style={{ position: 'relative', minHeight: '200px' }}>
          <AnimatePresence mode="wait">
            <motion.div
            key={pageKey}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 30
            }}
          >
            {/* Menu Items */}
            <div className="space-y-6 md:space-y-8">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-lg" style={{ color: styleConfig.descriptionColor }}>Nessun elemento trovato in questa categoria.</p>
                </div>
              ) : (
                items.map((item) => (
                  <div
                    key={item.id}
                    className="pb-2 md:pb-1"
                    style={{
                      border: styleConfig.showItemBorder ? `${styleConfig.cardBorderWidth || '1px'} solid ${styleConfig.cardBorderColor}` : 'none',
                      borderRadius: styleConfig.borderRadius,
                      padding: '1rem 2rem 1rem 1rem'
                    }}
                  >
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Item Image */}
                      {styleConfig.showImages && item.imageUrl && (
                        <div className="md:w-1/3">
                          <img
                            src={getFullImageUrl(item.imageUrl)}
                            alt={item.name}
                            className="w-full h-48 object-cover rounded-md"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      {/* Item Details */}
                      <div className={`flex-1 ${styleConfig.showImages && item.imageUrl ? 'md:w-2/3' : ''}`}>
                        <div className="flex items-center gap-2 w-full">
                          <h3 className="flex-[0_0_80%] min-w-0 break-words" style={{ color: styleConfig.dishColor, fontSize: styleConfig.fontSizeDish ?? styleConfig.fontSizeLarge, fontFamily: styleConfig.fontFamilyDish ?? styleConfig.fontFamily, fontWeight: styleConfig.fontWeightDish ?? '700' }}>
                            {item.name}
                          </h3>
                          <div className="flex-1 min-w-[8px] border-b border-dotted flex-shrink-0" style={{ borderColor: styleConfig.cardBorderColor }} />
                          {styleConfig.showPrices && item.price && (
                            <span className="whitespace-nowrap flex-[0_0_20%] text-right" style={{ color: styleConfig.priceColor, fontSize: styleConfig.fontSizePrice ?? styleConfig.fontSizeDish ?? styleConfig.fontSizeLarge, fontFamily: styleConfig.fontFamilyPrice ?? styleConfig.fontFamilyDish ?? styleConfig.fontFamily, fontWeight: styleConfig.fontWeightPrice ?? '700' }}>
                              {typeof item.price === 'number'
                                ? `€${item.price.toFixed(2)}`
                                : `${item.price}€`}
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="mt-1" style={{ color: styleConfig.descriptionColor, fontSize: styleConfig.fontSizeDescription ?? styleConfig.fontSizeMedium, fontFamily: styleConfig.fontFamilyDescription ?? styleConfig.fontFamily }}>
                            {item.description}
                          </p>
                        )}
                        
                        {/* Allergens */}
                        {styleConfig.showAllergens && (item.allergens || item.Allergens) && (
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-1 items-center">
                              {(item.allergens || item.Allergens)?.map((allergen) => {
                                const allergenKey = `${item.id}-${allergen.id}`;
                                const isNameVisible = visibleAllergenKey === allergenKey;
                                const displayName = getAllergenDisplayNameItalian(allergen);
                                return (
                                  <span
                                    key={allergen.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setVisibleAllergenKey(isNameVisible ? null : allergenKey)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVisibleAllergenKey(isNameVisible ? null : allergenKey); } }}
                                    className="inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded cursor-pointer select-none hover:opacity-90"
                                    style={{ fontSize: styleConfig.fontSizeSmall, color: styleConfig.allergenColor }}
                                    title={displayName}
                                  >
                                    <AllergenIcon allergen={allergen} alt={displayName} className="w-5 h-5" invert />
                                    {!getPublicAllergenIconPath(allergen.code) && !allergen.icon && displayName}
                                    {(getPublicAllergenIconPath(allergen.code) || allergen.icon) && isNameVisible && displayName && (
                                      <span className="font-medium whitespace-nowrap" style={{ fontSize: styleConfig.fontSizeSmall, color: styleConfig.priceColor }}>
                                        {displayName}
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 py-8" style={{ fontFamily: styleConfig.fontFamilyFooter ?? styleConfig.fontFamily, fontSize: styleConfig.fontSizeFooter ?? styleConfig.fontSizeBase }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col items-center text-center gap-6 md:gap-2">
            {/* Restaurant Logo - Moved to top */}
            {restaurantLogoUrl && (
              <img
                src={restaurantLogoUrl}
                alt={restaurant?.name}
                className="w-auto object-contain"
                style={{ height: logoHeightFooter }}
              />
            )}

            {/* Menu URL with larger text */}
            {restaurant?.website && (
              <a
                href={restaurant.website.startsWith('http') ? restaurant.website : `http://${restaurant.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline hover:opacity-80 break-all"
                style={{ color: styleConfig.secondaryColor }}
              > 
                {restaurant.website.replace(/^https?:\/\//, '')}
              </a>
            )}

            {/* Restaurant Contact Information */}
            <div className="text-center space-y-1" style={{ color: styleConfig.textColor }}>
              {restaurant?.name && (
                <h3 className="text-lg font-semibold" style={{ color: styleConfig.headerColor, fontFamily: styleConfig.fontFamilyFooter ?? styleConfig.fontFamily, fontSize: styleConfig.fontSizeFooter ?? styleConfig.fontSizeBase }}>{restaurant.name}</h3>
              )}
              {restaurant?.address && (
                <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>{restaurant.address}</p>
              )}
              {(restaurant?.city || restaurant?.country) && (
                <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>
                  {restaurant.city}{restaurant.city && restaurant.country ? ', ' : ''}{restaurant.country}
                </p>
              )}
              {restaurant?.phone && (
                <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>
                  <Phone className="w-4 h-4 inline mr-1" />
                  {restaurant.phone}
                </p>
              )}
              {restaurant?.email && (
                <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>
                  <Mail className="w-4 h-4 inline mr-1" />
                  {restaurant.email}
                </p>
              )}
            </div>

            {/* Menunumal branding - styled as in image */}
            <div className="space-y-4">
             
              
              {/* Allergen List - styled differently */}
              <button
                onClick={() => setShowAllergenModal(true)}
                className="text-sm md:text-base hover:underline font-medium text-white"
              >
                Lista allergeni e specifiche
              </button>

              {/* Privacy and Terms - no separator pipes */}
              <div className="flex flex-col items-center justify-center w-full gap-3 md:flex-row md:gap-6 text-xs md:text-sm text-[#aaaaaa]">
                <a
                  href={`/privacy-policy${slug ? `/${slug}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline hover:text-white transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  Privacy and Cookie Policy
                </a>
                <a
                  href={`/terms-and-conditions${slug ? `/${slug}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:underline hover:text-white transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Terms and Conditions
                </a>
              </div>

              {/* Social Icons - only show when at least one link is set */}
              {(socialLinks.tripadvisor || socialLinks.google || socialLinks.facebook || socialLinks.instagram) && (
                <div className="flex gap-4 md:gap-6 pt-4">
                  {socialLinks.tripadvisor && (
                    <a href={socialLinks.tripadvisor} target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-70">
                      <Star className="w-5 h-5 md:w-6 md:h-6" />
                    </a>
                  )}
                  {socialLinks.google && (
                    <a href={socialLinks.google} target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-70">
                      <div className="w-5 h-5 md:w-6 md:h-6">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
                        </svg>
                      </div>
                    </a>
                  )}
                  {socialLinks.facebook && (
                    <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-70">
                      <FacebookIcon className="w-5 h-5 md:w-6 md:h-6" />
                    </a>
                  )}
                  {socialLinks.instagram && (
                    <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-70">
                      <InstagramIcon className="w-5 h-5 md:w-6 md:h-6" />
                    </a>
                  )}
                </div>
              )}
            </div>
             <div className="flex items-center justify-center">
                <img
                  src="/images/kmzero-logo-dark.png"
                  alt="Menumal"
                  className="h-6 md:h-12 object-contain"
                />
              </div>
            <p className="text-xs opacity-70 mt-0" style={{ color: styleConfig.descriptionColor }}>
        Menù Digitale® è un prodotto Km Zero Web Marketing
      </p>
          </div>
        </div>
      </footer>
      </div>

      {/* Allergen Modal - MODIFIED: Pass menuId */}
      <AllergenModal
        isOpen={showAllergenModal}
        onClose={() => setShowAllergenModal(false)}
        styleConfig={styleConfig}
        menuId={menuData.id} // Pass the menuId from menuData
      />
    </div>
  );
};

export default PublicMenuPageDark;